import condriLogo from "@/assets/condri-without-background.png"
import { AlertTriangle, Mail, CheckCircle, Loader2 } from "lucide-react"
import { useState } from "react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/hooks/use-toast"
import { validateEmail } from "@/lib/validation"
import { Auth } from "@/heyapi/sdk.gen"
import { AppStoreButtons } from "@/components/ui/AppStores/AppStoreButtons"
import { safeError } from "@/utils/safeError"

const PasswordRecoveryPage = () => {
  const [email, setEmail] = useState("")
  const [emailSent, setEmailSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { toast } = useToast()

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    if (error) {
      setError(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate email
    const emailValidation = validateEmail(email)
    if (!emailValidation.isValid) {
      setError(emailValidation.error!)
      return
    }

    setLoading(true)
    try {
      await Auth.authPasswordRecovery({
        body: { email, link_target: "admin" },
      })
      setEmailSent(true)
      toast({
        title: "Password reset email sent!",
        description: `If an account exists for ${email}, you'll receive password reset instructions.`,
      })
    } catch (err: any) {
      console.error("Password recovery request failed:", safeError(err))
      // Always show success message to prevent user enumeration
      setEmailSent(true)
      toast({
        title: "Password reset email sent!",
        description: `If an account exists for ${email}, you'll receive password reset instructions.`,
      })
    } finally {
      setLoading(false)
    }
  }

  if (emailSent) {
    return (
      <div className="min-h-screen max-w-md mx-auto bg-card flex flex-col">
        <div className="flex-1 flex flex-col justify-center px-6 pt-8">
          <div className="text-center space-y-6">
            <div className="mx-auto w-20 h-20 bg-primary-faded rounded-full flex items-center justify-center p-2">
              <img
                src={condriLogo}
                alt="Condri Logo"
                className="w-full h-full object-contain"
              />
            </div>

            <div className="space-y-4">
              <div className="mx-auto w-16 h-16 bg-success/10 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-success" />
              </div>

              <div className="space-y-2">
                <h1 className="text-3xl font-bold text-foreground">
                  Check your email
                </h1>
                <p className="text-muted-foreground text-lg">
                  We've sent password reset instructions to{" "}
                  <strong>{email}</strong>
                </p>
                <p className="text-sm text-muted-foreground">
                  If an account exists for this email, you'll receive a link to
                  reset your password. The link will expire in a few hours.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 pb-6 space-y-4">
          <Button
            variant="outline"
            onClick={() => {
              setEmailSent(false)
              setEmail("")
            }}
            className="w-full"
          >
            Send to a different email
          </Button>
          <div className="pt-4">
            <p className="text-sm text-muted-foreground mb-4 text-center">
              Download the Condri app to sign in:
            </p>
            <AppStoreButtons />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen max-w-md mx-auto bg-card flex flex-col">
      <div className="flex-1 flex flex-col justify-center px-6 pt-8">
        <div className="text-center space-y-6">
          <div className="mx-auto w-20 h-20 bg-primary-faded rounded-full flex items-center justify-center p-2">
            <img
              src={condriLogo}
              alt="Condri Logo"
              className="w-full h-full object-contain"
            />
          </div>

          <div className="space-y-3">
            <h1 className="text-3xl font-bold text-foreground">
              Reset Password
            </h1>
            <p className="text-muted-foreground text-lg">
              Enter your email address and we'll send you a link to reset your
              password
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col px-6 pb-6 space-y-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={handleEmailChange}
                required
                disabled={loading}
                className="pl-10 transition-all duration-200 focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-gradient-hero hover:bg-gradient-hero/90 text-white font-medium py-3 rounded-lg transition-all duration-300 shadow-soft hover:shadow-glow"
            disabled={loading || !email.trim()}
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending reset link...
              </>
            ) : (
              <>
                <Mail className="mr-2 h-4 w-4" />
                Send reset link
              </>
            )}
          </Button>
        </form>

        <div className="text-center space-y-4">
          <p className="text-sm text-muted-foreground">
            Download the Condri app to sign in:
          </p>
          <AppStoreButtons />
        </div>
      </div>
    </div>
  )
}

export default PasswordRecoveryPage
